/**
 * EmailSendModeBanner - full-width strip at the top of every page
 * that warns when outbound email is being suppressed or redirected.
 *
 * Mounted in ``AppShell`` above the route ``<Outlet />`` so the
 * banner is visible regardless of which surface (agent, customer,
 * admin) the operator is on. The banner returns ``null`` in
 * production mode so the layout collapses to zero height.
 *
 * Two color variants per the plan (D10):
 *   - suppression: amber. "No outbound emails are being sent."
 *   - bypass:      red.   "All outbound emails are being redirected
 *                          to {bypass_recipient}."
 */
import { useEmailSendMode } from "@/hooks/useEmailSendMode";

export function EmailSendModeBanner(): JSX.Element | null {
  const query = useEmailSendMode();
  if (!query.data) return null;
  const { mode, bypass_recipient } = query.data;
  if (mode === "production") return null;

  if (mode === "suppression") {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="email-send-mode-banner"
        data-mode="suppression"
        className="border-b border-amber-300 bg-amber-100 px-4 py-2 text-center text-sm text-amber-900"
      >
        <i
          className="fa-duotone fa-thin fa-pause-circle mr-2"
          aria-hidden="true"
        />
        Email suppression mode is ON. No outbound emails will be sent.
      </div>
    );
  }

  // bypass
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="email-send-mode-banner"
      data-mode="bypass"
      className="border-b border-red-300 bg-red-100 px-4 py-2 text-center text-sm text-red-900"
    >
      <i
        className="fa-duotone fa-thin fa-share-from-square mr-2"
        aria-hidden="true"
      />
      Email bypass mode is ON. All outbound emails are being redirected
      to <span className="font-semibold">{bypass_recipient}</span>.
    </div>
  );
}

export default EmailSendModeBanner;
